import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface UniversityStats {
    total_enrolments: number;
    total_certificates: number;
    total_corporate_enquiries: number;
    active_monthly: number;
    active_annual: number;
    active_single: number;
    active_family: number;
    active_corporate: number;
    mrr: number;
}

interface Enrolment {
    id: string;
    user_id: string;
    class_id: string;
    mastery_score: number;
    last_active_at: string | null;
    progress_state: { total_lessons_completed: number } | null;
    user_email: string;
    user_name: string;
    degree_name: string;
}

interface CorporateEnquiry {
    id: string;
    name: string;
    email: string;
    company: string;
    seats: string;
    message: string | null;
    created_at: string;
    contacted: boolean | null;
}

interface NfpOrganisation {
    id: string;
    name: string;
    contact_name: string;
    contact_email: string;
    application_status: string;
    organisation_type: string;
    country: string;
    created_at: string;
    plan_key: string;
    max_seats: number;
    price_per_seat_cents: number;
}

interface ScholarshipApplication {
    id: string;
    applicant_name: string;
    applicant_email: string;
    status: string;
    story: string;
    cause_area: string;
    requested_plan: string;
    created_at: string;
}

interface ScholarshipBalance {
    total_donated_cents: number;
    total_allocated_cents: number;
    balance_cents: number;
    total_donors: number;
    total_recipients: number;
}

interface GiftEnrolment {
    id: string;
    buyer_name: string;
    buyer_email: string;
    recipient_name: string;
    recipient_email: string;
    amount_paid: number;
    plan_key: string;
    status: string;
    created_at: string;
    gift_code: string;
    redeemed: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function Pill({ color, children }: { color: string; children: React.ReactNode }) {
    const colours: Record<string, string> = {
        green: 'bg-emerald-100 text-emerald-300 border-emerald-200',
        gold: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        blue: 'bg-blue-100 text-sky-300 border-blue-200',
        purple: 'bg-purple-100 text-purple-700 border-purple-200',
        red: 'bg-red-100 text-red-700 border-red-200',
        slate: 'bg-slate-100 text-slate-400 border-slate-200',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${colours[color] ?? colours.slate}`}>
            {children}
        </span>
    );
}

function StatCard({
    icon, label, value, sub, color,
}: {
    icon: string; label: string; value: string | number; sub?: string; color: string;
}) {
    const bg: Record<string, string> = {
        emerald: 'from-emerald-950/40 to-slate-900/60 border-emerald-500/20',
        gold: 'from-amber-950/40 to-slate-900/60 border-amber-500/20',
        blue: 'from-blue-950/40 to-slate-900/60 border-blue-500/20',
        purple: 'from-purple-950/40 to-slate-900/60 border-purple-500/20',
        teal: 'from-teal-950/40 to-slate-900/60 border-teal-500/20',
    };
    const ic: Record<string, string> = {
        emerald: 'bg-emerald-500 shadow-emerald-500/20',
        gold: 'bg-yellow-500 shadow-yellow-500/20',
        blue: 'bg-blue-500 shadow-blue-500/20',
        purple: 'bg-purple-500 shadow-purple-500/20',
        teal: 'bg-teal-500 shadow-teal-500/20',
    };
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3, boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}
            className={`bg-gradient-to-br ${bg[color]} border rounded-3xl p-6 transition-shadow duration-200 backdrop-blur-md`}
        >
            <div className={`w-12 h-12 rounded-2xl ${ic[color]} shadow-lg flex items-center justify-center text-xl mb-4`}>
                {icon}
            </div>
            <p className="text-sm font-semibold text-muted-foreground mb-1">{label}</p>
            <p className="text-3xl font-black text-foreground tracking-tight">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-panels
// ─────────────────────────────────────────────────────────────────────────────

function StatsPanel({ stats }: { stats: UniversityStats | null }) {
    if (!stats) return null;
    return (
        <div className="space-y-8">
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <StatCard icon="🎓" label="Total Enrolments" value={stats?.total_enrolments ?? 0} color="emerald" />
                <StatCard icon="🏆" label="Certificates Issued" value={stats?.total_certificates ?? 0} color="gold" />
                <StatCard icon="🏢" label="Corporate Enquiries" value={stats?.total_corporate_enquiries ?? 0} color="teal" />
                <StatCard icon="💰" label="Est. MRR" value={`$${(stats?.mrr || 0).toLocaleString()}`} sub="monthly recurring" color="blue" />
                <StatCard icon="📋" label="Active Plans" value={(stats?.active_monthly || 0) + (stats?.active_annual || 0) + (stats?.active_single || 0) + (stats?.active_family || 0) + (stats?.active_corporate || 0)} color="purple" />
            </div>

            {/* Plan breakdown */}
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl border border-white/80 p-6">
                <h3 className="font-bold text-slate-800 mb-4 text-lg">Plan Breakdown</h3>
                <div className="space-y-3">
                    {[
                        { plan: 'Unlimited Monthly', key: 'active_monthly', price: 29, icon: '🔄', color: 'blue' },
                        { plan: 'Unlimited Annual', key: 'active_annual', price: 21, icon: '📅', color: 'emerald' },
                        { plan: 'Family Bundle (Legacy)', key: 'active_family', price: 79, icon: '👨‍👩‍👧', color: 'purple' },
                        { plan: 'Corporate', key: 'active_corporate', price: 35, icon: '🏢', color: 'teal' },
                        { plan: 'Single Degree (Legacy)', key: 'active_single', price: 29, icon: '📖', color: 'gold' },
                    ].map(({ plan, key, price, icon, color }) => {
                        const count = stats[key as keyof UniversityStats] as number;
                        const maxCount = Math.max(stats.active_monthly, stats.active_annual, stats.active_family, stats.active_corporate, stats.active_single, 1);
                        const pct = Math.round((count / maxCount) * 100);
                        const barColor: Record<string, string> = {
                            blue: 'bg-blue-500', emerald: 'bg-emerald-500', purple: 'bg-purple-500', teal: 'bg-teal-500', gold: 'bg-yellow-500',
                        };
                        return (
                            <div key={key} className="flex items-center gap-4">
                                <span className="text-lg w-6">{icon}</span>
                                <div className="w-36 text-sm font-medium text-white shrink-0">{plan}</div>
                                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${pct}%` }}
                                        transition={{ duration: 0.8, ease: 'easeOut' }}
                                        className={`h-full rounded-full ${barColor[color]}`}
                                    />
                                </div>
                                <span className="text-sm font-bold text-slate-800 w-8 text-right">{count}</span>
                                <span className="text-xs text-slate-400 w-20 text-right">${(count * price).toLocaleString()}/mo</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function EnrolmentsPanel() {
    const [enrolments, setEnrolments] = useState<Enrolment[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 15;

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const { data } = await supabase.functions.invoke('admin', {
                    body: { action: 'get_university_enrolments', page, page_size: PAGE_SIZE },
                });
                if (!cancelled) setEnrolments(data?.enrolments || []);
            } catch (err) {
                console.error('University enrolments load error:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [page]);

    const filtered = enrolments.filter(e =>
        e.user_email?.toLowerCase().includes(search.toLowerCase()) ||
        e.user_name?.toLowerCase().includes(search.toLowerCase()) ||
        e.degree_name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search student or degree…"
                        className="w-full pl-10 pr-4 py-2.5 bg-white/60 border border-white/80 rounded-xl text-sm text-slate-800 placeholder-slate-400 outline-none focus:border-emerald-300 transition"
                    />
                </div>
                <span className="text-sm text-slate-400 font-medium">{filtered.length} enrolments</span>
            </div>

            {/* Table */}
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl border border-white/80 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-20 text-slate-400">
                        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mr-3" />
                        Loading enrolments…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="text-4xl mb-3">🎓</div>
                        <p className="text-slate-400 font-medium">No enrolments yet</p>
                        <p className="text-slate-300 text-sm mt-1">Students will appear here once they enrol</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        {['Student', 'Degree', 'Progress', 'Mastery', 'Last Active'].map(h => (
                                            <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filtered.map((e, i) => {
                                        const lessons = e.progress_state?.total_lessons_completed || 0;
                                        return (
                                            <motion.tr
                                                key={e.id}
                                                initial={{ opacity: 0, x: -8 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.02 }}
                                                className="hover:bg-emerald-50/40 transition-colors"
                                            >
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                                            {(e.user_name || e.user_email || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-slate-800">{e.user_name || '—'}</p>
                                                            <p className="text-xs text-slate-400">{e.user_email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <p className="text-sm text-white font-medium max-w-[220px] truncate">
                                                        {e.degree_name?.replace('BSc ', '') || '—'}
                                                    </p>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-emerald-500 rounded-full"
                                                                style={{ width: `${Math.min(lessons * 5, 100)}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-xs text-slate-400">{lessons} lessons</span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Pill color={e.mastery_score >= 80 ? 'green' : e.mastery_score >= 60 ? 'gold' : 'slate'}>
                                                        {e.mastery_score}%
                                                    </Pill>
                                                </td>
                                                <td className="px-5 py-4 text-xs text-slate-400">
                                                    {e.last_active_at
                                                        ? new Date(e.last_active_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                                        : 'Never'}
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
                            <button
                                disabled={page === 0}
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                className="px-4 py-1.5 text-sm font-semibold text-slate-400 disabled:opacity-40 hover:bg-slate-100 rounded-lg transition"
                            >
                                ← Previous
                            </button>
                            <span className="text-sm text-slate-400">Page {page + 1}</span>
                            <button
                                disabled={enrolments.length < PAGE_SIZE}
                                onClick={() => setPage(p => p + 1)}
                                className="px-4 py-1.5 text-sm font-semibold text-slate-400 disabled:opacity-40 hover:bg-slate-100 rounded-lg transition"
                            >
                                Next →
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function CorporatePanel() {
    const [enquiries, setEnquiries] = useState<CorporateEnquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const { data } = await supabase.functions.invoke('admin', {
                    body: { action: 'get_corporate_enquiries' },
                });
                if (!cancelled) setEnquiries(data?.enquiries || []);
            } catch (err) {
                console.error('Corporate enquiries load error:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    async function toggleContacted(id: string, current: boolean) {
        await supabase
            .from('corporate_enquiries')
            .update({ contacted: !current } as any)
            .eq('id', id);
        setEnquiries(prev => prev.map(e => e.id === id ? { ...e, contacted: !current } : e));
    }

    if (loading) return (
        <div className="flex items-center justify-center py-20 text-slate-400">
            <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full mr-3" />
            Loading enquiries…
        </div>
    );

    if (enquiries.length === 0) return (
        <div className="text-center py-20 bg-white/60 rounded-3xl border border-white/80">
            <div className="text-4xl mb-3">🏢</div>
            <p className="text-slate-400 font-medium">No corporate enquiries yet</p>
            <p className="text-slate-300 text-sm mt-1">Submissions from the corporate landing page will appear here</p>
        </div>
    );

    return (
        <div className="space-y-3">
            {enquiries.map((enq, i) => (
                <motion.div
                    key={enq.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/80 overflow-hidden"
                >
                    {/* Header row */}
                    <button
                        onClick={() => setExpanded(expanded === enq.id ? null : enq.id)}
                        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-50/60 transition"
                    >
                        <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0">
                            {enq.company.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-slate-800">{enq.company}</p>
                                <Pill color="teal">{enq.seats} seats</Pill>
                                {enq.contacted
                                    ? <Pill color="green">✓ Contacted</Pill>
                                    : <Pill color="gold">⏳ Pending</Pill>}
                            </div>
                            <p className="text-sm text-slate-400 mt-0.5">{enq.name} · {enq.email}</p>
                        </div>
                        <div className="text-right shrink-0">
                            <p className="text-xs text-slate-400">
                                {new Date(enq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                            <p className="text-xs text-slate-300 mt-0.5">{expanded === enq.id ? '▲ collapse' : '▼ expand'}</p>
                        </div>
                    </button>

                    {/* Expanded detail */}
                    <AnimatePresence>
                        {expanded === enq.id && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-4">
                                    {/* Contact info */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        {[
                                            { label: 'Name', value: enq.name },
                                            { label: 'Email', value: enq.email },
                                            { label: 'Company', value: enq.company },
                                            { label: 'Seats', value: enq.seats },
                                        ].map(({ label, value }) => (
                                            <div key={label} className="bg-slate-50 rounded-xl p-3">
                                                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">{label}</p>
                                                <p className="font-semibold text-slate-800 truncate">{value}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Message */}
                                    {enq.message && (
                                        <div className="bg-slate-50 rounded-xl p-4">
                                            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-2">Message</p>
                                            <p className="text-sm text-white leading-relaxed">{enq.message}</p>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <a
                                            href={`mailto:${enq.email}?subject=Sage University — Corporate Enquiry&body=Hi ${enq.name},%0A%0AThank you for your interest in Sage University for ${enq.company}.`}
                                            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white text-sm font-bold rounded-xl hover:bg-teal-600 transition"
                                        >
                                            ✉️ Send Email
                                        </a>
                                        <button
                                            onClick={() => toggleContacted(enq.id, !!enq.contacted)}
                                            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-xl border transition ${enq.contacted
                                                ? 'border-slate-200 text-slate-400 hover:bg-slate-50'
                                                : 'border-emerald-200 text-emerald-300 bg-emerald-50 hover:bg-emerald-100'
                                                }`}
                                        >
                                            {enq.contacted ? '↩ Mark Uncontacted' : '✓ Mark Contacted'}
                                        </button>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(enq.email)}
                                            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-400 rounded-xl border border-slate-200 hover:bg-slate-50 transition"
                                        >
                                            📋 Copy Email
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            ))}
        </div>
    );
}

function CertificatesPanel() {
    const [certs, setCerts] = useState<{ id: string; cert_type: string; degree_slug: string | null; issued_at: string; user_email: string; user_name: string; verification_code: string }[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                // Direct query — university_certificates is readable by admin via RLS
                const { data } = await supabase
                    .from('university_certificates')
                    .select(`
            id, cert_type, degree_slug, issued_at, verification_code,
            profiles:user_id ( email, full_name )
          `)
                    .order('issued_at', { ascending: false })
                    .limit(50);

                if (cancelled) return;
                const mapped = (data || []).map((c: Record<string, unknown>) => {
                    const p = c.profiles as { email?: string; full_name?: string } | null;
                    return {
                        id: c.id as string,
                        cert_type: c.cert_type as string,
                        degree_slug: c.degree_slug as string | null,
                        issued_at: c.issued_at as string,
                        verification_code: c.verification_code as string,
                        user_email: p?.email || '—',
                        user_name: p?.full_name || '—',
                    };
                });
                setCerts(mapped);
            } catch (err) {
                console.error('Certs load error:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    const CERT_ICONS: Record<string, string> = { lesson: '📌', module: '📜', degree: '🎓', honours: '⭐' };
    const CERT_COLORS: Record<string, string> = { lesson: 'blue', module: 'gold', degree: 'green', honours: 'purple' };

    if (loading) return (
        <div className="flex items-center justify-center py-20 text-slate-400">
            <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full mr-3" />
            Loading certificates…
        </div>
    );

    if (certs.length === 0) return (
        <div className="text-center py-20 bg-white/60 rounded-3xl border border-white/80">
            <div className="text-4xl mb-3">🏆</div>
            <p className="text-slate-400 font-medium">No certificates issued yet</p>
            <p className="text-slate-300 text-sm mt-1">Certificates appear here as students complete modules and degrees</p>
        </div>
    );

    return (
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl border border-white/80 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-slate-100">
                            {['Type', 'Student', 'Degree / Module', 'Issued', 'Code'].map(h => (
                                <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-slate-400 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {certs.map((c, i) => (
                            <motion.tr
                                key={c.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: i * 0.015 }}
                                className="hover:bg-yellow-50/30 transition-colors"
                            >
                                <td className="px-5 py-4">
                                    <Pill color={CERT_COLORS[c.cert_type] || 'slate'}>
                                        {CERT_ICONS[c.cert_type]} {c.cert_type}
                                    </Pill>
                                </td>
                                <td className="px-5 py-4">
                                    <p className="text-sm font-semibold text-slate-800">{c.user_name}</p>
                                    <p className="text-xs text-slate-400">{c.user_email}</p>
                                </td>
                                <td className="px-5 py-4">
                                    <p className="text-sm text-slate-400 max-w-[200px] truncate">
                                        {(c.degree_slug || '').replace(/^uni-/, '').split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '—'}
                                    </p>
                                </td>
                                <td className="px-5 py-4 text-xs text-slate-400">
                                    {new Date(c.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </td>
                                <td className="px-5 py-4">
                                    <code className="text-xs bg-slate-100 rounded px-2 py-1 text-slate-400 font-mono">
                                        {c.verification_code}
                                    </code>
                                </td>
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// NFP, Scholarship & Gifts
// ─────────────────────────────────────────────────────────────────────────────

function NfpPanel() {
    const [nfps, setNfps] = useState<NfpOrganisation[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const { data } = await supabase.functions.invoke('admin', { body: { action: 'get_nfp_organisations' } });
            if (data?.data) setNfps(data.data);
        } catch (err) {
            console.error('NFP load error:', err);
        } finally {
            setLoading(false);
        }
    }

    async function updateStatus(id: string, status: string) {
        try {
            await supabase.functions.invoke('admin', { body: { action: 'update_nfp_organisation', id, application_status: status } });
            load();
        } catch (e) { console.error(e); }
    }

    if (loading) return <div className="p-8 text-center text-slate-400">Loading NFPs...</div>;

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800">NFP Organisations</h3>
            <div className="bg-white/60 rounded-3xl border overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b text-left text-xs text-slate-400 uppercase">
                            <th className="p-4">Organisation</th>
                            <th className="p-4">Contact</th>
                            <th className="p-4">Type / Country</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {nfps.map(n => (
                            <tr key={n.id} className="hover:bg-slate-50">
                                <td className="p-4 cursor-pointer font-bold text-slate-800">{n.name}</td>
                                <td className="p-4 text-sm text-slate-400">{n.contact_name}<br /><span className="text-xs text-slate-400">{n.contact_email}</span></td>
                                <td className="p-4 text-sm text-slate-400">{n.organisation_type} ({n.country})</td>
                                <td className="p-4">
                                    <Pill color={n.application_status === 'approved' ? 'green' : n.application_status === 'pending' ? 'gold' : 'slate'}>{n.application_status}</Pill>
                                </td>
                                <td className="p-4 flex gap-2">
                                    {n.application_status === 'pending' && (
                                        <>
                                            <button onClick={() => updateStatus(n.id, 'approved')} className="px-3 py-1 bg-emerald-100 text-emerald-300 rounded-lg text-xs font-bold">Approve</button>
                                            <button onClick={() => updateStatus(n.id, 'rejected')} className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-bold">Reject</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ScholarshipsPanel() {
    const [balance, setBalance] = useState<ScholarshipBalance | null>(null);
    const [apps, setApps] = useState<ScholarshipApplication[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const { data } = await supabase.functions.invoke('admin', { body: { action: 'get_scholarships' } });
            if (data?.data) {
                setBalance(data.data.balance);
                setApps(data.data.applications);
            }
        } catch (err) {
            console.error('Scholarships load error:', err);
        } finally {
            setLoading(false);
        }
    }

    async function allocate(id: string) {
        if (!window.confirm('Allocate scholarship to this applicant? This will generate a gift code and deduct from the fund.')) return;
        try {
            await supabase.functions.invoke('admin', { body: { action: 'allocate_scholarship', application_id: id, amount_cents: 4900 } });
            load();
        } catch (e) { console.error(e); }
    }

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Scholarships...</div>;

    return (
        <div className="space-y-6">
            {balance && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatCard icon="💰" label="Total Donated" value={`$${(balance.total_donated_cents / 100).toFixed(2)}`} color="emerald" />
                    <StatCard icon="🎓" label="Total Allocated" value={`$${(balance.total_allocated_cents / 100).toFixed(2)}`} color="blue" />
                    <StatCard icon="⚖️" label="Current Balance" value={`$${(balance.balance_cents / 100).toFixed(2)}`} color="purple" />
                </div>
            )}

            <h3 className="text-lg font-bold text-slate-800">Applications</h3>
            <div className="bg-white/60 rounded-3xl border overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b text-left text-xs text-slate-400 uppercase">
                            <th className="p-4">Applicant</th>
                            <th className="p-4">Cause Area</th>
                            <th className="p-4">Story</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {apps.map(a => (
                            <tr key={a.id} className="hover:bg-slate-50">
                                <td className="p-4 text-sm font-bold text-slate-800">{a.applicant_name}<br /><span className="font-normal text-xs text-slate-400">{a.applicant_email}</span></td>
                                <td className="p-4 text-sm text-slate-400">{a.cause_area}</td>
                                <td className="p-4 text-xs text-slate-400 max-w-xs">{a.story}</td>
                                <td className="p-4">
                                    <Pill color={a.status === 'approved' ? 'green' : 'gold'}>{a.status}</Pill>
                                </td>
                                <td className="p-4">
                                    {a.status === 'pending' && (
                                        <button onClick={() => allocate(a.id)} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-bold">Approve & Allocate</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function GiftsPanel() {
    const [gifts, setGifts] = useState<GiftEnrolment[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const { data } = await supabase.functions.invoke('admin', { body: { action: 'get_gift_enrolments' } });
            if (data?.data) setGifts(data.data);
        } catch (err) {
            console.error('Gifts load error:', err);
        } finally {
            setLoading(false);
        }
    }

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Gifts...</div>;

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800">Gift Enrolments</h3>
            <div className="bg-white/60 rounded-3xl border overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b text-left text-xs text-slate-400 uppercase">
                            <th className="p-4">Buyer</th>
                            <th className="p-4">Recipient</th>
                            <th className="p-4">Plan / Amount</th>
                            <th className="p-4">Code / Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {gifts.map(g => (
                            <tr key={g.id} className="hover:bg-slate-50">
                                <td className="p-4 text-sm text-slate-800">{g.buyer_name || '—'}<br /><span className="text-xs text-slate-400">{g.buyer_email}</span></td>
                                <td className="p-4 text-sm text-slate-800">{g.recipient_name || '—'}<br /><span className="text-xs text-slate-400">{g.recipient_email}</span></td>
                                <td className="p-4 text-sm text-slate-400">{g.plan_key}<br /><span className="text-xs font-bold">${(g.amount_paid / 100).toFixed(2)}</span></td>
                                <td className="p-4">
                                    <code className="block mb-1 px-2 py-0.5 w-max bg-slate-100 text-white rounded text-xs tracking-wider">{g.gift_code}</code>
                                    <Pill color={g.status === 'redeemed' || g.redeemed ? 'green' : 'blue'}>{g.status}</Pill>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
type Panel = 'overview' | 'enrolments' | 'corporate' | 'certificates' | 'nfps' | 'scholarships' | 'gifts';

export const UniversityManagement: React.FC = () => {
    const [stats, setStats] = useState<UniversityStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [panel, setPanel] = useState<Panel>('overview');

    useEffect(() => {
        let cancelled = false;
        async function loadStats() {
            setStatsLoading(true);
            try {
                const { data } = await supabase.functions.invoke('admin', {
                    body: { action: 'get_university_stats' },
                });
                if (!cancelled && data) setStats(data);
            } catch (err) {
                console.error('University stats error:', err);
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        }
        loadStats();
        return () => { cancelled = true; };
    }, []);

    const panels: { id: Panel; label: string; icon: string }[] = [
        { id: 'overview', label: 'Overview', icon: '📊' },
        { id: 'enrolments', label: 'Enrolments', icon: '🎓' },
        { id: 'corporate', label: 'Corporate', icon: '🏢' },
        { id: 'certificates', label: 'Certificates', icon: '🏆' },
        { id: 'nfps', label: 'NFPs', icon: '🤝' },
        { id: 'scholarships', label: 'Scholarships', icon: '⚖️' },
        { id: 'gifts', label: 'Gifts', icon: '🎁' },
    ];

    return (
        <div className="p-6 lg:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center text-xl shadow-lg shadow-yellow-200">
                            🏛️
                        </div>
                        <h2 className="text-2xl font-black text-white">University Management</h2>
                    </div>
                    <p className="text-slate-400 text-sm ml-13">
                        Sage University · {statsLoading ? '…' : stats?.total_enrolments ?? 0} students enrolled
                    </p>
                </div>

                {/* Quick links */}
                <div className="flex gap-2">
                    <a
                        href="/university"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-400 bg-white/60 border border-white/80 rounded-xl hover:bg-slate-50 transition"
                    >
                        🌐 View Landing Page
                    </a>
                    <a
                        href="/university/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-400 bg-white/60 border border-white/80 rounded-xl hover:bg-slate-50 transition"
                    >
                        📚 Student Dashboard
                    </a>
                </div>
            </div>

            {/* Panel tabs */}
            <div className="flex gap-2 flex-wrap">
                {panels.map(p => (
                    <button
                        key={p.id}
                        onClick={() => setPanel(p.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${panel === p.id
                            ? 'bg-slate-900 text-white shadow-md'
                            : 'bg-white/60 text-slate-400 border border-white/80 hover:bg-white hover:text-slate-800'
                            }`}
                    >
                        <span>{p.icon}</span>
                        {p.label}
                        {p.id === 'corporate' && stats && stats.total_corporate_enquiries > 0 && (
                            <span className="bg-teal-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                                {stats.total_corporate_enquiries}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Panel content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={panel}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                >
                    {panel === 'overview' && (statsLoading
                        ? <div className="flex items-center justify-center py-20 text-slate-400">
                            <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full mr-3" />
                            Loading stats…
                        </div>
                        : <StatsPanel stats={stats} />
                    )}
                    {panel === 'enrolments' && <EnrolmentsPanel />}
                    {panel === 'corporate' && <CorporatePanel />}
                    {panel === 'certificates' && <CertificatesPanel />}
                    {panel === 'nfps' && <NfpPanel />}
                    {panel === 'scholarships' && <ScholarshipsPanel />}
                    {panel === 'gifts' && <GiftsPanel />}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default UniversityManagement;